<template>
  <div>
    <div v-if="loading" class="text-center p-5">
      <b-spinner variant="info" />
    </div>
    <b-alert v-else-if="loadError" show variant="danger"
      >{{ $t("label.certificateLoadError") }}
      <b-button size="sm" variant="outline-danger" @click="loadCertificate">{{
        $t("label.retryPermissionDefinitions")
      }}</b-button></b-alert
    >
    <form v-else @submit.prevent="submit">
      <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
      <fieldset :disabled="saving || readOnly || !canWrite">
        <b-form-group :label="$t('label.name')"
          ><b-form-input v-model.trim="form.name" required
        /></b-form-group>
        <b-form-group :label="$t('label.certificateBackground')">
          <b-form-file
            v-if="!readOnly && canWrite"
            v-model="file"
            :required="!certificateUUID"
            accept="image/png,image/jpeg,image/webp"
          />
          <b-form-text>{{ $t("label.certificateImageHint") }}</b-form-text>
        </b-form-group>
        <b-alert show variant="info">{{
          $t("label.certificateTemplateNotice")
        }}</b-alert>
        <p class="small text-muted">{{ $t("label.certificateLayoutHint") }}</p>
        <div class="row">
          <div
            v-for="key in layoutKeys"
            :key="key"
            class="col-12 col-lg-6 mb-3"
          >
            <div class="border rounded p-3 h-100">
              <h6>{{ $t(fieldLabels[key]) }}</h6>
              <b-form-checkbox
                v-if="key !== 'recipient_name'"
                v-model="form.layoutConfig[key].enabled"
                class="mb-3"
                @change="drag = null"
                >{{ $t("label.certificateShowField") }}</b-form-checkbox
              >
              <div v-if="form.layoutConfig[key].enabled" class="row">
                <div
                  v-for="field in numericFields"
                  :key="field.key"
                  class="col-6"
                >
                  <b-form-group :label="$t(field.label)"
                    ><b-form-input
                      v-model.number="form.layoutConfig[key][field.key]"
                      type="number"
                      :min="field.min"
                      :max="field.max"
                      step="any"
                      required
                  /></b-form-group>
                </div>
                <div class="col-12">
                  <b-form-group :label="$t('label.certificateFontFamily')"
                    ><b-form-select
                      v-model="form.layoutConfig[key].font_family"
                      :options="fontOptions(key)"
                      required
                  /></b-form-group>
                </div>
                <div class="col-6">
                  <b-form-group :label="$t('label.certificateColor')"
                    ><b-form-input
                      v-model="form.layoutConfig[key].color"
                      type="color"
                      required
                  /></b-form-group>
                </div>
                <div class="col-6">
                  <b-form-group :label="$t('label.certificateAlign')"
                    ><b-form-select
                      v-model="form.layoutConfig[key].align"
                      :options="alignOptions"
                  /></b-form-group>
                </div>
              </div>
            </div>
          </div>
        </div>
        <b-form-group :label="$t('label.certificatePreview')">
          <div v-if="previewSource" class="certificate-preview border rounded">
            <img
              :src="previewSource"
              :alt="form.name"
              @load="imageLoaded"
              @error="imageError = true"
            />
            <svg
              v-if="!imageError"
              ref="canvas"
              :viewBox="`0 0 ${imageWidth} ${imageHeight}`"
              class="certificate-overlay"
              @pointermove="moveText"
              @pointerup="stopDrag"
              @pointercancel="stopDrag"
              @lostpointercapture="drag = null"
            >
              <g
                v-for="key in enabledLayoutKeys"
                :key="key"
                class="certificate-text"
                :class="{ 'certificate-text-selected': selectedKey === key }"
                tabindex="0"
                role="button"
                :aria-label="$t(fieldLabels[key])"
                @focus="selectedKey = key"
                @pointerdown.prevent="startDrag(key, $event)"
                @keydown="nudgeText(key, $event)"
              >
                <rect
                  :x="(form.layoutConfig[key].x * imageWidth) / 100"
                  :y="(form.layoutConfig[key].y * imageHeight) / 100"
                  :width="(form.layoutConfig[key].width * imageWidth) / 100"
                  :height="
                    Math.max(24, Number(form.layoutConfig[key].font_size) * 1.4)
                  "
                />
                <text
                  :x="textX(form.layoutConfig[key])"
                  :y="(form.layoutConfig[key].y * imageHeight) / 100"
                  :font-size="form.layoutConfig[key].font_size"
                  :font-family="previewFont(form.layoutConfig[key].font_family)"
                  :fill="form.layoutConfig[key].color"
                  :text-anchor="textAnchor(form.layoutConfig[key].align)"
                  dominant-baseline="hanging"
                >
                  {{ $t(fieldLabels[key]) }}
                </text>
              </g>
            </svg>
          </div>
          <b-form-text v-if="imageError" class="text-danger">{{
            $t("label.certificatePreviewError")
          }}</b-form-text>
          <b-form-text>{{ $t("label.certificatePreviewHint") }}</b-form-text>
        </b-form-group>
      </fieldset>
      <hr />
      <div class="d-flex justify-content-end">
        <b-button
          type="button"
          variant="outline-secondary"
          class="mr-2"
          :disabled="saving"
          @click="$emit('done')"
          >{{ $t("label.close") }}</b-button
        >
        <b-button
          v-if="!readOnly && canWrite"
          type="submit"
          variant="info"
          :disabled="saving"
          ><b-spinner v-if="saving" small /><span v-else>{{
            $t(certificateUUID ? "label.updateX" : "label.createX", [
              $t("module.certificate"),
            ])
          }}</span></b-button
        >
      </div>
    </form>
    <div v-if="loading || loadError" class="text-right mt-3">
      <b-button variant="outline-secondary" @click="$emit('done')">{{
        $t("label.close")
      }}</b-button>
    </div>
  </div>
</template>
<script>
import { certificateService } from "@/services/api/certificate.service";
import {
  certificateModel,
  certificateDefaultLayout,
  validCertificateLayout,
  certificateFontFamilies,
  certificatePreviewFont,
  certificateFieldKeys,
  certificateFieldLabels,
} from "@/models/api/certificate.model";
import { alertHelper, sharedHelper, sessionHelper } from "@/utils";
const imageTypes = ["image/png", "image/jpeg", "image/webp"];
export default {
  name: "CertificateForm",
  props: {
    readOnly: { type: Boolean, default: false },
    certificateUUID: { type: String, default: "" },
  },
  data() {
    return {
      form: { name: "", layoutConfig: certificateDefaultLayout() },
      file: null,
      existingImage: "",
      previewURL: "",
      imageWidth: 1200,
      imageHeight: 850,
      imageError: false,
      drag: null,
      selectedKey: "recipient_name",
      loading: Boolean(this.certificateUUID),
      loadError: false,
      saving: false,
      error: "",
      requestId: 0,
      layoutKeys: certificateFieldKeys,
      fieldLabels: certificateFieldLabels,
    };
  },
  computed: {
    canWrite() {
      const user = this.$store.state.api.auth.user?.user;
      return (
        sessionHelper.getUserPermissions(user?.permissions, user?.isSuperadmin)
          .cert_admin === 2
      );
    },
    enabledLayoutKeys() {
      return this.layoutKeys.filter(
        (key) => this.form.layoutConfig[key].enabled,
      );
    },
    previewSource() {
      return this.previewURL || this.existingImage;
    },
    numericFields() {
      return [
        { key: "x", label: "label.certificateX", min: 0, max: 100 },
        { key: "y", label: "label.certificateY", min: 0, max: 100 },
        { key: "width", label: "label.certificateWidth", min: 0.01, max: 100 },
        { key: "font_size", label: "label.certificateFontSize", min: 0.01 },
      ];
    },
    alignOptions() {
      return ["left", "center", "right"].map((value) => ({
        value,
        text: this.$t(`label.certificateAlign${value}`),
      }));
    },
  },
  watch: {
    file(value) {
      if (this.previewURL) URL.revokeObjectURL(this.previewURL);
      this.previewURL =
        value && imageTypes.includes(value.type)
          ? URL.createObjectURL(value)
          : "";
      this.imageError = false;
    },
  },
  created() {
    if (this.certificateUUID) this.loadCertificate();
  },
  beforeDestroy() {
    this.requestId++;
    if (this.previewURL) URL.revokeObjectURL(this.previewURL);
  },
  methods: {
    previewFont: certificatePreviewFont,
    fontOptions() {
      return certificateFontFamilies;
    },
    pointerPosition(event) {
      const bounds = this.$refs.canvas.getBoundingClientRect();
      return {
        x: ((event.clientX - bounds.left) / bounds.width) * 100,
        y: ((event.clientY - bounds.top) / bounds.height) * 100,
      };
    },
    startDrag(key, event) {
      if (
        !this.form.layoutConfig[key].enabled ||
        this.saving ||
        this.readOnly ||
        sessionHelper.getPermissionLevel("cert_admin") !== 2 ||
        event.button !== 0 ||
        this.drag
      )
        return;
      const item = this.form.layoutConfig[key],
        point = this.pointerPosition(event);
      this.selectedKey = key;
      this.drag = {
        key,
        pointerId: event.pointerId,
        dx: point.x - Number(item.x),
        dy: point.y - Number(item.y),
      };
      this.$refs.canvas.setPointerCapture(event.pointerId);
    },
    setPosition(key, x, y) {
      const item = this.form.layoutConfig[key];
      item.x =
        Math.round(Math.max(0, Math.min(100 - Number(item.width), x)) * 100) /
        100;
      const maxY = Math.max(
        0,
        100 -
          (Math.max(24, Number(item.font_size) * 1.4) / this.imageHeight) * 100,
      );
      item.y = Math.round(Math.max(0, Math.min(maxY, y)) * 100) / 100;
    },
    moveText(event) {
      if (
        !this.drag ||
        event.pointerId !== this.drag.pointerId ||
        this.saving ||
        this.readOnly ||
        sessionHelper.getPermissionLevel("cert_admin") !== 2
      )
        return;
      const point = this.pointerPosition(event);
      this.setPosition(
        this.drag.key,
        point.x - this.drag.dx,
        point.y - this.drag.dy,
      );
    },
    stopDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.drag = null;
      if (this.$refs.canvas.hasPointerCapture(event.pointerId))
        this.$refs.canvas.releasePointerCapture(event.pointerId);
    },
    nudgeText(key, event) {
      if (
        this.saving ||
        this.readOnly ||
        sessionHelper.getPermissionLevel("cert_admin") !== 2 ||
        !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
      )
        return;
      event.preventDefault();
      const item = this.form.layoutConfig[key],
        step = event.shiftKey ? 1 : 0.1;
      this.setPosition(
        key,
        Number(item.x) +
          (event.key === "ArrowRight"
            ? step
            : event.key === "ArrowLeft"
            ? -step
            : 0),
        Number(item.y) +
          (event.key === "ArrowDown"
            ? step
            : event.key === "ArrowUp"
            ? -step
            : 0),
      );
    },
    imageLoaded(event) {
      this.imageWidth = event.target.naturalWidth;
      this.imageHeight = event.target.naturalHeight;
      this.imageError = false;
    },
    textAnchor(align) {
      return (
        { left: "start", center: "middle", right: "end" }[align] || "start"
      );
    },
    textX(item) {
      return (
        ((Number(item.x) +
          Number(item.width) *
            (item.align === "center" ? 0.5 : item.align === "right" ? 1 : 0)) *
          this.imageWidth) /
        100
      );
    },
    async loadCertificate() {
      const request = ++this.requestId;
      this.loading = true;
      this.loadError = false;
      try {
        const result = await certificateService.show(this.certificateUUID);
        if (!result?.success) throw new Error("Invalid certificate details.");
        const raw = result.data;
        if (!raw || raw.uuid !== this.certificateUUID)
          throw new Error("Invalid certificate details.");
        if (request !== this.requestId) return;
        const item = certificateModel(raw);
        this.form = { name: item.name, layoutConfig: item.layoutConfig };
        this.existingImage = item.templatePath;
      } catch (error) {
        if (request === this.requestId) this.loadError = true;
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
    async submit() {
      if (
        this.saving ||
        this.readOnly ||
        sessionHelper.getPermissionLevel("cert_admin") !== 2
      )
        return;
      this.error = "";
      if (!this.form.name.trim() || (!this.certificateUUID && !this.file)) {
        this.error = this.$t("label.certificateRequired");
        return;
      }
      if (this.file && !imageTypes.includes(this.file.type)) {
        this.error = this.$t("label.certificateImageError");
        return;
      }
      if (!validCertificateLayout(this.form.layoutConfig)) {
        this.error = this.$t("label.certificateLayoutError");
        return;
      }
      this.saving = true;
      this.$emit("saving", true);
      try {
        const data = { ...this.form, file: this.file };
        const result = this.certificateUUID
          ? await certificateService.update(this.certificateUUID, data)
          : await certificateService.store(data);
        if (!result?.success)
          throw new Error(result?.message || this.$t("message.unknownError"));
        alertHelper.alertSuccess({
          message: this.$t(
            this.certificateUUID
              ? "message.updateXSuccess"
              : "message.createXSuccess",
            [this.$t("module.certificate")],
          ),
        });
        this.$emit("saved");
      } catch (error) {
        this.error =
          sharedHelper.getErrorMessage(error) ||
          this.$t("message.unknownError");
      } finally {
        this.saving = false;
        this.$emit("saving", false);
      }
    },
  },
};
</script>
<style scoped>
.certificate-preview {
  position: relative;
  overflow: hidden;
}
.certificate-preview img {
  display: block;
  width: 100%;
  height: auto;
}
.certificate-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  touch-action: none;
}
.certificate-text {
  cursor: grab;
  outline: none;
}
.certificate-text rect {
  fill: rgba(0, 163, 255, 0.04);
  stroke: #8ba2ac;
  stroke-width: 1;
  stroke-dasharray: 6 4;
}
.certificate-text-selected rect,
.certificate-text:focus rect {
  stroke: #00a3ff;
  stroke-width: 2;
}
.certificate-text:active {
  cursor: grabbing;
}
</style>
